import { EllipsisOutlined } from '@ant-design/icons'
import { Button, Popconfirm, Popover, Space, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { TABLE_MAX_HEIGHT } from '@/constants/customer'
import routes from '@/constants/routes'
import { WashingMachinePart } from '@/types'
import { GetWashingMachinePartResponse } from '@/types/apis/washingMachinePart/GetWashingMachinePartResponse'
import { formatJapaneseNumber } from '@/utils/formatPrice'

import { CustomRowTable } from './styles'

type Props = {
  dataSource: GetWashingMachinePartResponse | undefined
  getWashingMachinePartLoading: boolean
  onDelete: (id: WashingMachinePart['id']) => void
}

const WashingMachinePartTable = ({ dataSource, getWashingMachinePartLoading, onDelete }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [visibleConfirmDelete, setVisibleConfirmDelete] = useState<Record<string, boolean>>({})

  const columns: ColumnsType<WashingMachinePart> = [
    {
      title: t('components.pages.washingMachinePart.list.table.name'),
      dataIndex: 'name',
      key: 'name',
      className: 'nowrap',
      render: (name: string) => {
        return <CustomRowTable type="left">{name}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.washingMachinePart.list.table.manufacture'),
      dataIndex: 'manufacturer',
      key: 'manufacturer',
      className: 'nowrap',
      render: (manufacture: string) => {
        return <CustomRowTable type="left">{manufacture}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.washingMachinePart.list.table.supplier'),
      dataIndex: 'supplier',
      key: 'supplier',
      className: 'nowrap',
      render: (supplier: string) => {
        return <CustomRowTable type="left">{supplier}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.washingMachinePart.list.table.price'),
      dataIndex: 'price',
      key: 'price',
      className: 'nowrap',
      render: (price: number) => {
        return <CustomRowTable type="right">{price ? formatJapaneseNumber(price) : 0}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.washingMachinePart.list.table.inventory'),
      dataIndex: 'inventory',
      key: 'inventory',
      className: 'nowrap',
      render: (inventory: number) => {
        return <CustomRowTable type="right">{inventory ? formatJapaneseNumber(inventory) : 0}</CustomRowTable>
      },
      align: 'center' as const,
    },
  ]

  columns.push({
    align: 'center' as const,
    render: (record: WashingMachinePart) => {
      return (
        <Space size="middle">
          <Popover
            placement="bottomRight"
            content={
              <Space size="small" direction="vertical">
                <Button size="small" onClick={() => goToEditWashingMachinePartPage(record.id)}>
                  {t('components.pages.admins.list.table.update')}
                </Button>
                <Popconfirm
                  id={record.id.toString()}
                  visible={!!visibleConfirmDelete[record.id]}
                  title={t('components.pages.attendanceLogs.list.table.confirmDelete')}
                  okText={t('common.yes')}
                  cancelText={t('common.no')}
                  onConfirm={() => {
                    onDelete(record.id)
                  }}
                  onCancel={() => setVisibleConfirmDelete({})}
                >
                  <Button
                    size="small"
                    danger
                    onClick={() => setVisibleConfirmDelete({ [record.id]: true })}
                    loading={false}
                  >
                    {t('components.pages.admins.list.table.delete')}
                  </Button>
                </Popconfirm>
              </Space>
            }
          >
            <Button>
              <EllipsisOutlined />
            </Button>
          </Popover>
        </Space>
      )
    },
  })

  const goToEditWashingMachinePartPage = (id: WashingMachinePart['id']) => {
    const path = routes.updateWashingMachinePart.path.replace(':id', id.toString())
    navigate(path)
  }

  return (
    <Table
      rowKey="id"
      bordered
      dataSource={dataSource}
      columns={columns}
      loading={getWashingMachinePartLoading}
      pagination={false}
      scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
    />
  )
}

WashingMachinePartTable.displayName = 'WashingMachinePartTable'
export default WashingMachinePartTable
